"use client";

import { useState } from "react";
import Link from "next/link";

import { track } from "@/lib/analytics/track";
import { consultListedAmount } from "@/lib/offers/fixed-offers";
import { formatScanPrice, scanOptions, type ScanOptionId } from "@/lib/offers/scan-pricing";
import CheckoutPayButton from "@/components/site/checkout/CheckoutPayButton";

/**
 * Scan option picker and order summary. Checkout requires configured
 * Stripe price IDs or a checkout URL. The prices themselves are published.
 */
export default function ScanPicker({
  checkoutUrl,
  bookingUrl,
  checkoutApi = false,
}: {
  checkoutUrl: string | null;
  bookingUrl: string | null;
  /** When true, the pay button creates a Checkout Session for the selected scan. */
  checkoutApi?: boolean;
}) {
  const [pick, setPick] = useState<ScanOptionId>("scan");
  const chosen = scanOptions.find((option) => option.id === pick) ?? scanOptions[0];
  const consultPrice = consultListedAmount();

  return (
    <div className="row" style={{ gap: 32, alignItems: "flex-start" }}>
      <div className="stack g12" role="radiogroup" aria-label="Scan option" style={{ flex: "999 1 520px", minWidth: 0 }}>
        {scanOptions.map((option) => {
          const on = option.id === pick;
          return (
            <button
              key={option.id}
              className={on ? "opt on" : "opt"}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => {
                if (option.id === pick) return;
                setPick(option.id);
                track("scan_option_select", { option: option.id, price: option.amount });
              }}
              style={{ padding: 24 }}
            >
              <span className="radio" aria-hidden="true" />
              <span className="stack g8" style={{ flex: 1 }}>
                <span className="row justify-between" style={{ gap: 12 }}>
                  <span className="display text-[22px] font-bold text-white">{option.name}</span>
                  <span className="price price-sm">{formatScanPrice(option.amount)}</span>
                </span>
                <span className="small" style={{ color: "#cbd5e1" }}>
                  {option.summary}
                </span>
              </span>
            </button>
          );
        })}
      </div>
      <aside
        className="panel topbar flex flex-col gap-4"
        style={{ flex: "1 1 320px", background: "#0f172a", padding: 28 }}
        aria-label="Order summary"
      >
        <h3 className="h4">{chosen.name}</h3>
        <div className="row justify-between" style={{ alignItems: "baseline" }}>
          <span className="small">Due today</span>
          <span className="price">{formatScanPrice(chosen.amount)}</span>
        </div>
        {checkoutApi ? (
          <>
            <CheckoutPayButton
              className="btn btn-primary btn-block"
              label={`Pay ${formatScanPrice(chosen.amount)}`}
              request={{
                product: "scan",
                tier: chosen.id === "fix" ? "fix" : "scan",
                expectedAmount: chosen.amount * 100,
              }}
              analyticsParams={{ product: chosen.product, price: chosen.amount, value: chosen.amount, currency: "USD" }}
            />
            <span className="caption">
              Secure checkout with Stripe. Next, you share your repo URL and access.{" "}
              <Link href="/refund-policy">Refund policy</Link>
            </span>
          </>
        ) : checkoutUrl ? (
          <>
            <a
              className="btn btn-primary btn-block"
              href={checkoutUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                track("checkout_click", {
                  product: chosen.product,
                  price: chosen.amount,
                  value: chosen.amount,
                  currency: "USD",
                })
              }
            >
              Pay {formatScanPrice(chosen.amount)}
            </a>
            <span className="caption">
              Secure checkout with Stripe. Next, you share your repo URL and access.{" "}
              <Link href="/refund-policy">Refund policy</Link>
            </span>
          </>
        ) : (
          <>
            <button className="btn btn-block is-disabled" type="button" disabled>
              Pay {formatScanPrice(chosen.amount)}
            </button>
            <span className="caption">Checkout is unavailable right now. Send details and I&#8217;ll follow up.</span>
          </>
        )}
        <div className="grid grid-cols-2 gap-2">
          <Link className="btn btn-outline" href="/contact?offer=scan" style={{ padding: "10px 12px" }}>
            Send details
          </Link>
          {bookingUrl ? (
            <a
              className="btn btn-outline"
              href={bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ padding: "10px 12px" }}
              onClick={() =>
                track("booking_click", {
                  product: "consult",
                  ...(consultPrice !== null ? { price: consultPrice, value: consultPrice } : {}),
                  currency: "USD",
                  location: "scan_picker",
                })
              }
            >
              Book a call
            </a>
          ) : (
            <Link
              className="btn btn-outline"
              href="/contact"
              style={{ padding: "10px 12px" }}
              onClick={() => track("booking_fallback_click", { location: "scan_picker" })}
            >
              Book a call
            </Link>
          )}
        </div>
      </aside>
    </div>
  );
}
